import { z } from 'zod';
// Import the leaf schema directly to avoid the
// `conversation-types ↔ n8n-webhook-payload-schema` cycle (P12-FU-3).
import { ConversationModeSchema } from './conversation-mode-schema';
import type { ConversationWebhookResponse } from './conversation-types';
import { SnapshotActionSchema } from './v2-schemas';

// =============================================================================
// CANONICAL CONVERSATION EVENT (Cloud → n8n)
// =============================================================================

export const ConversationEventSchema = z.object({
  turn_id: z.string().uuid(),
  conversation_id: z.string(),
  timestamp: z.string().datetime(),
  event: z.enum(['session.start', 'message', 'session.end']),
  role: z.enum(['user', 'system']),
  input_type: z.enum(['voice', 'text']),
  transcript: z.string(),
  // Multi-step turn cursor: 0 for the user-initiated turn, incremented per
  // mid-turn LLM call when the previous response set end_turn=false.
  // Real plumbing arrives in Phase 13a Task 3 (Cloud orchestration loop);
  // current callers may pass 0 or omit the field entirely.
  step_index: z.number().int().nonnegative().default(0),
  context: z.object({
    session: z.object({
      project_id: z.string(),
      mode: ConversationModeSchema,
      modality: z.enum(['voice', 'text']),
    }),
    identity: z.object({
      profile_id: z.string(),
      org_id: z.string(),
    }),
    activity_id: z.string().uuid().optional(),
    participant_id: z.string().uuid().optional(),
  }),
});
export type ConversationEvent = z.infer<typeof ConversationEventSchema>;

// =============================================================================
// CANONICAL CONTENT BLOCKS (n8n → Cloud)
// =============================================================================

const TextBlockSchema = z.object({
  type: z.literal('text'),
  text: z.string(),
});

/**
 * A widget action button. The canonical machine-readable identifier field is
 * `action`. Conversation guides have historically instructed the LLM to emit
 * `id` (alongside an `icon`) instead — e.g. the scoping decision card's
 * `{ "id": "approve", "label": "Looks right", "icon": "CheckCircle2" }`. The
 * gateway passes n8n `content` through unvalidated, so without normalisation
 * the client read `action === undefined` and every such button was inert. The
 * `z.preprocess` below aliases `id` → `action` when `action` is absent so the
 * button wires up regardless of which field the producer used.
 */
export const WidgetActionSchema = z.preprocess(
  (raw) => {
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const o = raw as Record<string, unknown>;
      if (o.action === undefined && typeof o.id === 'string') {
        return { ...o, action: o.id };
      }
    }
    return raw;
  },
  z.object({
    label: z.string().describe('Button text shown to user'),
    action: z.string().describe('Machine-readable action identifier'),
    variant: z.enum(['primary', 'secondary', 'ghost']).default('secondary').describe('Visual button style'),
    payload: z.record(z.unknown()).optional().describe('Arbitrary data passed back with the action callback'),
    text: z.string().optional().describe('Synthetic user message sent on click; falls back to label if absent.'),
    icon: z.string().optional().describe('Optional Lucide icon name supplied by the producer (guide).'),
  }),
);
export type WidgetAction = z.infer<typeof WidgetActionSchema>;

/**
 * Normalise a raw `actions` array — from an n8n widget block (the gateway
 * passes n8n `content` through unvalidated) or a hydrated/persisted transcript
 * — into canonical `WidgetAction`s: aliasing `id`→`action`, applying defaults,
 * and dropping any malformed entry. Safe on non-arrays (returns []). Use this
 * wherever `WidgetBlock.actions` is consumed for rendering, so a button's
 * `action` identifier is always defined regardless of which field shape the
 * producer emitted.
 */
export function normalizeWidgetActions(raw: unknown): WidgetAction[] {
  if (!Array.isArray(raw)) return [];
  const out: WidgetAction[] = [];
  for (const item of raw) {
    const parsed = WidgetActionSchema.safeParse(item);
    if (parsed.success) out.push(parsed.data);
  }
  return out;
}

const WidgetBlockSchema = z.object({
  type: z.literal('widget'),
  widget_type: z.string().describe('Widget type identifier'),
  widget_id: z.string().describe('Unique instance identifier'),
  data: z.record(z.unknown()).describe('Widget-specific props, validated at render time'),
  actions: z.array(WidgetActionSchema).optional().describe('Action buttons rendered below the widget'),
});

const LayoutActionBlockSchema = z.object({
  type: z.literal('layout_action'),
  widget_id: z.string(),
  action: z.enum(['resize', 'hide', 'show', 'remove']),
  params: z.record(z.unknown()).optional(),
});

export const ContentBlockSchema = z.discriminatedUnion('type', [
  TextBlockSchema,
  WidgetBlockSchema,
  LayoutActionBlockSchema,
]);
export type ContentBlock = z.infer<typeof ContentBlockSchema>;
export type TextBlock = z.infer<typeof TextBlockSchema>;
export type WidgetBlock = z.infer<typeof WidgetBlockSchema>;
export type LayoutActionBlock = z.infer<typeof LayoutActionBlockSchema>;

// =============================================================================
// CANONICAL RESPONSE ENVELOPE
// =============================================================================

export const CanonicalResponseSchema = z
  .object({
    conversation_id: z.string(),
    content: z.array(ContentBlockSchema),
    // When false, the Cloud orchestration loop (Phase 13a Task 3) re-invokes
    // the LLM with the resulting state/snapshot deltas applied — enabling
    // mid-turn tool/widget steps without yielding back to the user.
    end_turn: z.boolean().default(true),
    end_session: z.boolean().optional().default(false),
    /**
     * Why the session is ending (only meaningful when `end_session` is true).
     * `guardrail` = a prompt-injection / out-of-scope attempt tripped the
     * hardened guardrail; the LLM declined and the session is terminated. Cloud
     * relays the `content` message, marks the conversation ended, and the UI
     * surfaces a "Start again" affordance instead of a normal completion.
     * Absent/`completed` = a normal end. Optional + additive for back-compat.
     */
    end_reason: z.enum(['completed', 'guardrail']).optional(),
    // Phase 13a Task 12(a-pre): RFC 7396 JSON Merge Patch deltas. n8n returns
    // sparse patches; the Cloud orchestration loop (`runTurn`) merges each
    // delta onto the running state/snapshot and threads the merged value
    // forward into the next loop iteration's payload. Per RFC 7396, a `null`
    // value inside the delta deletes the corresponding key.
    state_delta: z
      .record(z.unknown())
      .optional()
      .describe(
        'RFC 7396 JSON Merge Patch for state. Cloud merges onto running state and threads forward into the next loop iteration.'
      ),
    snapshot_delta: z
      .record(z.unknown())
      .optional()
      .describe(
        'RFC 7396 JSON Merge Patch for snapshot. Same merge semantics as state_delta.'
      ),
    // Home mode may hand off mid-turn to scoping (no project) or update (project UUID).
    mode_change: z.enum(['scoping', 'update']).optional(),
    target_project_id: z.string().uuid().optional(),
    // Up to 3 contextual next-action pills returned by the LLM; rendered below the turn.
    turn_actions: z.array(SnapshotActionSchema).max(3).optional(),
    // 015 US4 — optional human-readable label shown while the turn runs (e.g.
    // "Looking up more information"). Emitted by n8n when the Agent node makes
    // MCP tool calls. Replaces the default "Thinking" indicator client-side.
    // Top-level (not a tts/ui action); omitted when not applicable. Max 80 chars.
    status_label: z.string().min(1).max(80).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.mode_change === 'update' && data.target_project_id === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'target_project_id is required when mode_change is "update" so Cloud can open the correct project context.',
        path: ['target_project_id'],
      });
    }
  });
export type CanonicalResponse = z.infer<typeof CanonicalResponseSchema>;

// =============================================================================
// V2 CONVERSATION TURN
// =============================================================================

export const V2ConversationTurnSchema = z.object({
  turn_id: z.string().describe('Groups related messages into one visual turn'),
  role: z.enum(['user', 'assistant']),
  content: z.array(ContentBlockSchema).describe('Ordered content blocks'),
  timestamp: z.string().datetime(),
  // Per-turn next actions attached by the gateway after the turn completes.
  actions: z.array(SnapshotActionSchema).optional(),
});
export type V2ConversationTurn = z.infer<typeof V2ConversationTurnSchema>;

// =============================================================================
// MIGRATION MAPPER: Old webhook response → Canonical response
// =============================================================================

/**
 * Maps the existing n8n ConversationWebhookResponse (actions array with
 * tts/ui/end types) to the new canonical response (content blocks).
 *
 * Used during migration while n8n still returns the old format.
 */
export function mapWebhookResponseToCanonical(
  webhookResponse: ConversationWebhookResponse
): CanonicalResponse {
  const content: ContentBlock[] = [];
  let endSession = webhookResponse.end_session ?? false;

  // n8n now returns canonical responses with `content` blocks; legacy callers
  // may still hand us a `{ actions: [...] }` shape. Default to an empty array
  // so canonical responses don't crash the mapper — the canonical fast-path
  // in the gateway handles `content` directly before reaching here.
  for (const action of webhookResponse.actions ?? []) {
    switch (action.type) {
      case 'tts': {
        const text = action.payload?.text;
        if (typeof text === 'string' && text.length > 0) {
          content.push({ type: 'text', text });
        }
        break;
      }
      case 'ui': {
        const event = action.payload?.event as string | undefined;
        const data = (action.payload?.data as Record<string, unknown>) ?? {};
        if (event) {
          content.push({
            type: 'widget',
            widget_type: event,
            widget_id: `ui_${Date.now()}_${content.length}`,
            data,
          });
        }
        break;
      }
      case 'end':
        endSession = true;
        break;
    }
  }

  return {
    conversation_id: webhookResponse.conversation_id ?? '',
    content,
    end_turn: true,
    end_session: endSession,
  };
}
